import React, { useState, useEffect } from 'react'
import { 
  Settings, Building2, Save, RotateCcw, 
  Upload, Download, Globe, Mail, Phone,
  Clock, FileText, Shield, Database, MessageCircle,
  Cog, ArrowRight
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import { useToast } from '../components/Toast'

import settingsService, { EmpresaSettings, SystemSettings, ConfigOptions } from '../services/settings'

const SettingsPage: React.FC = () => {
  const { success, error } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Estados
  const [empresaSettings, setEmpresaSettings] = useState<EmpresaSettings | null>(null)
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null)
  const [activeTab, setActiveTab] = useState<'empresa' | 'sistema' | 'gestao'>('empresa')
  const [nifValid, setNifValid] = useState<boolean | null>(null)

  // Queries
  const { data: configOptions, isLoading: optionsLoading } = useQuery({
    queryKey: ['config-options'],
    queryFn: () => settingsService.getConfigOptions()
  })

  const { data: empresaData, isLoading: empresaLoading } = useQuery({
    queryKey: ['empresa-settings'],
    queryFn: () => settingsService.getEmpresaSettings()
  })

  const { data: systemData, isLoading: systemLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => settingsService.getSystemSettings()
  })

  // Mutations
  const updateEmpresaMutation = useMutation({
    mutationFn: (settings: EmpresaSettings) => settingsService.updateEmpresaSettings(settings),
    onSuccess: () => {
      success('Configurações da empresa atualizadas com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['empresa-settings'] })
    },
    onError: (err: any) => {
      error('Erro ao atualizar configurações', err.message)
    }
  })

  const updateSystemMutation = useMutation({
    mutationFn: (settings: SystemSettings) => settingsService.updateSystemSettings(settings),
    onSuccess: () => {
      success('Configurações do sistema atualizadas com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['system-settings'] })
    },
    onError: (err: any) => {
      error('Erro ao atualizar configurações', err.message)
    }
  })

  const createBackupMutation = useMutation({
    mutationFn: () => settingsService.createBackup(),
    onSuccess: (data) => {
      success(`Backup criado com sucesso! ${data.backupFile}`)
    },
    onError: (err: any) => {
      error('Erro ao criar backup', err.message)
    }
  })

  const resetMutation = useMutation({
    mutationFn: () => settingsService.resetToDefaults(),
    onSuccess: () => {
      success('Configurações resetadas com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['empresa-settings'] })
      queryClient.invalidateQueries({ queryKey: ['system-settings'] })
    },
    onError: (err: any) => {
      error('Erro ao resetar configurações', err.message)
    }
  })

  // Efeitos
  useEffect(() => {
    if (empresaData) {
      setEmpresaSettings(empresaData)
    }
  }, [empresaData])

  useEffect(() => {
    if (systemData) {
      setSystemSettings(systemData)
    }
  }, [systemData])

  // Handlers
  const handleSaveEmpresa = () => {
    if (!empresaSettings) return

    // Validar dados
    const errors = []
    if (!empresaSettings.nome) errors.push('Nome da empresa é obrigatório')
    if (!empresaSettings.nif) errors.push('NIF é obrigatório')
    if (!settingsService.validateNifLocal(empresaSettings.nif)) errors.push('NIF inválido')
    if (!empresaSettings.email || !settingsService.validateEmail(empresaSettings.email)) errors.push('Email inválido')
    if (!settingsService.validateCodigoPostal(empresaSettings.codigoPostal)) errors.push('Código postal inválido')

    if (errors.length > 0) {
      error('Dados inválidos', errors.join(', '))
      return
    }

    updateEmpresaMutation.mutate(empresaSettings)
  }

  const handleSaveSystem = () => {
    if (!systemSettings) return
    updateSystemMutation.mutate(systemSettings)
  }

  const handleNifChange = async (nif: string) => {
    if (!empresaSettings) return

    setEmpresaSettings({ ...empresaSettings, nif })

    if (nif.length === 9) {
      const isValid = settingsService.validateNifLocal(nif)
      setNifValid(isValid)
    } else {
      setNifValid(null)
    }
  }

  const updateEmpresaField = (field: keyof EmpresaSettings, value: any) => {
    if (!empresaSettings) return
    setEmpresaSettings({ ...empresaSettings, [field]: value })
  }

  const updateSystemField = (field: keyof SystemSettings, value: any) => {
    if (!systemSettings) return
    setSystemSettings({ ...systemSettings, [field]: value })
  }

  const updateNumeracaoField = (type: 'numeracaoOS' | 'numeracaoVenda', field: string, value: any) => {
    if (!systemSettings) return
    setSystemSettings({
      ...systemSettings,
      [type]: {
        ...(systemSettings[type] || {}),
        [field]: value
      }
    })
  }

  const updateHorarioField = (field: string, value: string) => {
    if (!empresaSettings) return
    setEmpresaSettings({
      ...empresaSettings,
      horariosAtendimento: {
        ...(empresaSettings.horariosAtendimento || {}),
        [field]: value
      }
    })
  }

  const tabs = [
    { id: 'empresa' as const, label: 'Empresa', icon: Building2 },
    { id: 'sistema' as const, label: 'Sistema', icon: Settings },
    { id: 'gestao' as const, label: 'Gestão', icon: Cog }
  ]

  if (empresaLoading || systemLoading || optionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">A carregar configurações...</div>
      </div>
    )
  }

  if (!empresaSettings || !systemSettings || !configOptions) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Erro ao carregar configurações</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Settings className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
              <p className="text-gray-600">Gerir configurações da empresa e sistema</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => createBackupMutation.mutate()}
              leftIcon={<Download className="h-4 w-4" />}
              isLoading={createBackupMutation.isPending}
            >
              Backup
            </Button>
            <Button
              variant="outline"
              onClick={() => resetMutation.mutate()}
              leftIcon={<RotateCcw className="h-4 w-4" />}
              isLoading={resetMutation.isPending}
            >
              Resetar
            </Button>
            {activeTab !== 'gestao' && (
              <Button
                onClick={activeTab === 'empresa' ? handleSaveEmpresa : handleSaveSystem}
                leftIcon={<Save className="h-4 w-4" />}
                isLoading={updateEmpresaMutation.isPending || updateSystemMutation.isPending}
              >
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'empresa' && (
              <div className="space-y-6">
                {/* Dados Básicos */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Dados da Empresa
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Nome da Empresa *"
                      value={empresaSettings.nome}
                      onChange={(e) => updateEmpresaField('nome', e.target.value)}
                      placeholder="Nome da sua empresa"
                    />
                    <Input
                      label="Nome Comercial"
                      value={empresaSettings.nomeComercial || ''}
                      onChange={(e) => updateEmpresaField('nomeComercial', e.target.value)}
                      placeholder="Nome comercial (se diferente)"
                    />
                    <div>
                      <Input
                        label="NIF *"
                        value={empresaSettings.nif}
                        onChange={(e) => handleNifChange(e.target.value)}
                        placeholder="123456789"
                        maxLength={9}
                      />
                      {nifValid === false && (
                        <p className="text-sm text-red-600 mt-1">NIF inválido</p>
                      )}
                      {nifValid === true && (
                        <p className="text-sm text-green-600 mt-1">NIF válido</p>
                      )}
                    </div>
                    <Input
                      label="Atividade *"
                      value={empresaSettings.atividade}
                      onChange={(e) => updateEmpresaField('atividade', e.target.value)}
                      placeholder="Reparação de equipamentos eletrónicos"
                    />
                    <Input
                      label="CAE"
                      value={empresaSettings.cae || ''}
                      onChange={(e) => updateEmpresaField('cae', e.target.value)}
                      placeholder="95210"
                    />
                    <Select
                      label="Regime de IVA"
                      value={empresaSettings.regimeIva}
                      onChange={(e) => updateEmpresaField('regimeIva', e.target.value)}
                      options={configOptions.regimesIva}
                    />
                    <Input
                      label="IVA (%)"
                      type="number"
                      value={empresaSettings.ivaPercentual}
                      onChange={(e) => updateEmpresaField('ivaPercentual', parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                </div>

                {/* Contatos */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Contatos
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Telefone Principal *"
                      value={empresaSettings.telefone}
                      onChange={(e) => updateEmpresaField('telefone', e.target.value)}
                      placeholder="+351 912 345 678"
                      leftIcon={<Phone className="h-4 w-4" />}
                    />
                    <Input
                      label="Telefone Secundário"
                      value={empresaSettings.telefoneSecundario || ''}
                      onChange={(e) => updateEmpresaField('telefoneSecundario', e.target.value)}
                      placeholder="+351 213 456 789"
                      leftIcon={<Phone className="h-4 w-4" />}
                    />
                    <Input
                      label="Email *"
                      type="email"
                      value={empresaSettings.email}
                      onChange={(e) => updateEmpresaField('email', e.target.value)}
                      placeholder="contato@empresa.pt"
                      leftIcon={<Mail className="h-4 w-4" />}
                    />
                    <Input
                      label="Website"
                      value={empresaSettings.website || ''}
                      onChange={(e) => updateEmpresaField('website', e.target.value)}
                      placeholder="https://www.empresa.pt"
                      leftIcon={<Globe className="h-4 w-4" />}
                    />
                  </div>
                </div>

                {/* Morada */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Morada</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Morada *"
                      value={empresaSettings.morada}
                      onChange={(e) => updateEmpresaField('morada', e.target.value)}
                      placeholder="Rua da Empresa, 123"
                      className="md:col-span-2"
                    />
                    <Input
                      label="Código Postal *"
                      value={empresaSettings.codigoPostal}
                      onChange={(e) => updateEmpresaField('codigoPostal', e.target.value)}
                      placeholder="1234-567"
                    />
                    <Input
                      label="Cidade *"
                      value={empresaSettings.cidade}
                      onChange={(e) => updateEmpresaField('cidade', e.target.value)}
                      placeholder="Lisboa"
                    />
                    <Select
                      label="Distrito *"
                      value={empresaSettings.distrito}
                      onChange={(e) => updateEmpresaField('distrito', e.target.value)}
                      options={configOptions.distritos.map(d => ({ value: d, label: d }))}
                    />
                    <Input
                      label="País"
                      value={empresaSettings.pais}
                      onChange={(e) => updateEmpresaField('pais', e.target.value)}
                      disabled
                    />
                  </div>
                </div>

                {/* Horários */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Horários de Atendimento
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Segunda a Sexta *"
                      value={empresaSettings.horariosAtendimento?.segundaASexta || ''}
                      onChange={(e) => updateHorarioField('segundaASexta', e.target.value)}
                      placeholder="09:00-18:00"
                    />
                    <Input
                      label="Sábado"
                      value={empresaSettings.horariosAtendimento?.sabado || ''}
                      onChange={(e) => updateHorarioField('sabado', e.target.value)}
                      placeholder="09:00-13:00"
                    />
                    <Input
                      label="Domingo"
                      value={empresaSettings.horariosAtendimento?.domingo || ''}
                      onChange={(e) => updateHorarioField('domingo', e.target.value)}
                      placeholder="Fechado"
                    />
                    <Input
                      label="Feriados"
                      value={empresaSettings.horariosAtendimento?.feriados || ''}
                      onChange={(e) => updateHorarioField('feriados', e.target.value)}
                      placeholder="Fechado"
                    />
                  </div>
                </div>

                {/* Garantias */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Garantias e Prazos
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Tempo de Garantia (meses)"
                      type="number"
                      value={empresaSettings.tempoGarantia}
                      onChange={(e) => updateEmpresaField('tempoGarantia', parseInt(e.target.value) || 0)}
                      min="1"
                      max="36"
                    />
                    <Input
                      label="Prazo de Reparação (dias úteis)"
                      type="number"
                      value={empresaSettings.prazoReparacao}
                      onChange={(e) => updateEmpresaField('prazoReparacao', parseInt(e.target.value) || 0)}
                      min="1"
                      max="30"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sistema' && (
              <div className="space-y-6">
                {/* Localização */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Localização e Formato
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Moeda"
                      value={systemSettings.moeda}
                      onChange={(e) => updateSystemField('moeda', e.target.value)}
                      options={configOptions.moedas}
                    />
                    <Select
                      label="Idioma"
                      value={systemSettings.idioma}
                      onChange={(e) => updateSystemField('idioma', e.target.value)}
                      options={configOptions.idiomas}
                    />
                    <Select
                      label="Timezone"
                      value={systemSettings.timezone}
                      onChange={(e) => updateSystemField('timezone', e.target.value)}
                      options={configOptions.timezones}
                    />
                    <Select
                      label="Formato de Data"
                      value={systemSettings.formatoData}
                      onChange={(e) => updateSystemField('formatoData', e.target.value)}
                      options={configOptions.formatosData}
                    />
                    <Select
                      label="Formato de Hora"
                      value={systemSettings.formatoHora}
                      onChange={(e) => updateSystemField('formatoHora', e.target.value)}
                      options={configOptions.formatosHora}
                    />
                  </div>
                </div>

                {/* Numeração */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Numeração de Documentos
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3">Ordens de Serviço</h4>
                      <div className="space-y-3">
                        <Input
                          label="Prefixo"
                          value={systemSettings.numeracaoOS?.prefixo || ''}
                          onChange={(e) => updateNumeracaoField('numeracaoOS', 'prefixo', e.target.value)}
                          placeholder="OS"
                        />
                        <Input
                          label="Tamanho"
                          type="number"
                          value={systemSettings.numeracaoOS?.tamanho || ''}
                          onChange={(e) => updateNumeracaoField('numeracaoOS', 'tamanho', parseInt(e.target.value) || 6)}
                          min="4"
                          max="10"
                        />
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={systemSettings.numeracaoOS?.reiniciarAnualmente || false}
                            onChange={(e) => updateNumeracaoField('numeracaoOS', 'reiniciarAnualmente', e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Reiniciar anualmente</span>
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-3">Vendas</h4>
                      <div className="space-y-3">
                        <Input
                          label="Prefixo"
                          value={systemSettings.numeracaoVenda?.prefixo || ''}
                          onChange={(e) => updateNumeracaoField('numeracaoVenda', 'prefixo', e.target.value)}
                          placeholder="V"
                        />
                        <Input
                          label="Tamanho"
                          type="number"
                          value={systemSettings.numeracaoVenda?.tamanho || ''}
                          onChange={(e) => updateNumeracaoField('numeracaoVenda', 'tamanho', parseInt(e.target.value) || 6)}
                          min="4"
                          max="10"
                        />
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={systemSettings.numeracaoVenda?.reiniciarAnualmente || false}
                            onChange={(e) => updateNumeracaoField('numeracaoVenda', 'reiniciarAnualmente', e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Reiniciar anualmente</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Backup */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Backup Automático
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={systemSettings.backupAutomatico}
                        onChange={(e) => updateSystemField('backupAutomatico', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span>Ativar backup automático</span>
                    </label>
                    
                    {systemSettings.backupAutomatico && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Intervalo (horas)"
                          type="number"
                          value={systemSettings.intervaloBackup}
                          onChange={(e) => updateSystemField('intervaloBackup', parseInt(e.target.value) || 24)}
                          min="1"
                          max="168"
                        />
                        <Input
                          label="Manter Backups"
                          type="number"
                          value={systemSettings.manterBackups}
                          onChange={(e) => updateSystemField('manterBackups', parseInt(e.target.value) || 7)}
                          min="1"
                          max="30"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Segurança */}
                <div>
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Segurança
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Sessão Expira (minutos)"
                      type="number"
                      value={systemSettings.sessaoExpira}
                      onChange={(e) => updateSystemField('sessaoExpira', parseInt(e.target.value) || 480)}
                      min="30"
                      max="1440"
                    />
                    <Input
                      label="Tentativas de Login"
                      type="number"
                      value={systemSettings.tentativasLogin}
                      onChange={(e) => updateSystemField('tentativasLogin', parseInt(e.target.value) || 3)}
                      min="2"
                      max="10"
                    />
                    <Input
                      label="Bloqueio (minutos)"
                      type="number"
                      value={systemSettings.bloqueioTempo}
                      onChange={(e) => updateSystemField('bloqueioTempo', parseInt(e.target.value) || 15)}
                      min="5"
                      max="60"
                    />
                  </div>
                </div>
              </div>
            )}


            {activeTab === 'gestao' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Configurações de Gestão
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Configure os elementos do sistema de gestão
                  </p>
                </div>

                {/* Cards de configurações */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Estados de Ordens de Serviço */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-gray-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Settings className="h-6 w-6 text-blue-600" />
                          </div>
                          <h4 className="text-lg font-medium text-gray-900">
                            Estados de OS
                          </h4>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Configure os estados disponíveis para as ordens de serviço
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/configuracoes/estados-os')}
                          rightIcon={<ArrowRight className="h-4 w-4" />}
                        >
                          Configurar Estados
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Placeholder para outras configurações futuras */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 opacity-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <Cog className="h-6 w-6 text-gray-400" />
                          </div>
                          <h4 className="text-lg font-medium text-gray-500">
                            Outras Configurações
                          </h4>
                        </div>
                        <p className="text-gray-400 text-sm mb-4">
                          Funcionalidades adicionais em breve...
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                        >
                          Em desenvolvimento
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage